import { ingredientDisplayName, purchaseFormatLabel } from './costing';
import type { Ingredient } from './types';

/**
 * Lectura de la hoja de recuento rellenada a mano (mejora 40, Alex, 4 sep
 * 2026): se imprime la hoja, el equipo cuenta con boligrafo, se sube la foto
 * y Alexia propone las cantidades. PROPONE: nada entra en el recuento hasta
 * que alguien lo revisa en pantalla, igual que con las facturas.
 *
 * El OCR devuelve filas de texto (`ScannedSheet`); aqui se casan con los
 * ingredientes del restaurante y se interpretan los numeros. Es puro y vive
 * en shared para poder probarlo sin AWS y para que la demo recorra el mismo
 * camino que la app real.
 */

export interface ScannedSheetRow {
  /** Celdas de izquierda a derecha, tal y como las leyo el OCR. */
  cells: string[];
  /** Confianza minima de la fila (0-100), si el motor la da. */
  confidence?: number;
}

export interface ScannedSheet {
  rows: ScannedSheetRow[];
}

export type ConfianzaLinea = 'alta' | 'media' | 'baja';

export interface InventoryDraftLine {
  ingredientId: string;
  name: string;
  /** "Garrafa de 15 L": lo que se cuenta en la hoja. */
  formato: string;
  /** Cantidad leida, EN FORMATOS DE COMPRA (como en la hoja). Ausente = casilla vacia. */
  qty?: number;
  /** El texto tal cual, para que el usuario vea que se leyo. */
  leido?: string;
  confianza: ConfianzaLinea;
}

export interface InventoryDraft {
  lines: InventoryDraftLine[];
  /** Filas de la hoja que no casan con ningun ingrediente. */
  sinEmparejar: Array<{ texto: string; leido?: string }>;
  avisos: string[];
}

/* ------------------------------------------------------------------ */
/* Texto                                                               */
/* ------------------------------------------------------------------ */

export function normalizarTexto(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[…]/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * La cantidad escrita a mano: "2,5", "2.5", "12", "1/2", "½", "3 cajas".
 * Devuelve undefined si no hay ningun numero (casilla vacia o tachada).
 */
export function parseCantidad(raw: string | undefined): number | undefined {
  if (!raw) return undefined;
  let s = raw.trim().replace(/½/g, '0,5').replace(/¼/g, '0,25').replace(/¾/g, '0,75');
  // Fracciones escritas: 1/2, 1 1/2
  const fraccion = /^(\d+)?\s*(\d+)\s*\/\s*(\d+)$/.exec(s);
  if (fraccion) {
    const entero = Number(fraccion[1] ?? 0);
    const den = Number(fraccion[3]);
    if (den > 0) return Math.round((entero + Number(fraccion[2]) / den) * 1000) / 1000;
  }
  // El primer numero que aparezca, con coma o punto decimal.
  const m = /-?\d+(?:[.,]\d+)?/.exec(s);
  if (!m) return undefined;
  s = m[0].replace(',', '.');
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.round(n * 1000) / 1000;
}

/** Una celda que es "solo numero" (con lo que un boligrafo le anyade). */
const esNumerica = (s: string): boolean => /^[\s\d.,/½¼¾-]+$/.test(s) && /\d|[½¼¾]/.test(s);

/* ------------------------------------------------------------------ */
/* Emparejar con los ingredientes                                      */
/* ------------------------------------------------------------------ */

interface Candidato {
  ing: Ingredient;
  /** Formas normalizadas por las que se puede reconocer. */
  claves: string[];
  tokens: Set<string>;
}

function candidatos(ingredients: Ingredient[]): Candidato[] {
  return ingredients.map((ing) => {
    const claves = [
      ingredientDisplayName(ing),
      [ing.name, ing.brand].filter(Boolean).join(' '),
      ing.name,
    ].map(normalizarTexto);
    return { ing, claves, tokens: new Set(claves[0].split(' ').filter((t) => t.length > 1)) };
  });
}

function solape(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) return 0;
  let comunes = 0;
  for (const t of a) if (b.has(t)) comunes++;
  return comunes / Math.max(a.size, b.size);
}

/**
 * Busca el ingrediente de una fila. Primero por igualdad (la hoja imprime el
 * nombre completo, el OCR lo suele leer bien), luego por prefijo (la hoja
 * recorta los nombres largos con "…") y por ultimo por solape de palabras,
 * que es lo que salva un "Aceite oliva virgen" leido como "Aceite olive virgen".
 */
function emparejar(
  texto: string,
  lista: Candidato[],
  usados: Set<string>,
): { cand: Candidato; exacto: boolean } | undefined {
  const t = normalizarTexto(texto);
  if (t === '') return undefined;
  const libres = lista.filter((c) => !usados.has(c.ing.ingredientId));

  const exacto = libres.find((c) => c.claves.includes(t));
  if (exacto) return { cand: exacto, exacto: true };

  const prefijo = libres.find((c) => t.length >= 6 && c.claves.some((k) => k.startsWith(t)));
  if (prefijo) return { cand: prefijo, exacto: true };

  const tokens = new Set(t.split(' ').filter((x) => x.length > 1));
  let mejor: { cand: Candidato; puntos: number } | undefined;
  for (const c of libres) {
    const puntos = solape(tokens, c.tokens);
    if (puntos >= 0.5 && (!mejor || puntos > mejor.puntos)) mejor = { cand: c, puntos };
  }
  return mejor ? { cand: mejor.cand, exacto: mejor.puntos >= 0.9 } : undefined;
}

const CABECERAS = ['ingrediente', 'formato de compra', 'cantidad contada', 'cantidad'];

/**
 * De las filas leidas a una propuesta de recuento. Devuelve TODAS las lineas
 * que ha reconocido, tambien las que quedaron en blanco (una casilla vacia
 * quiere decir "no queda", y hay que ensenarlo como tal, no esconderlo).
 */
export function buildInventoryDraft(sheet: ScannedSheet, ingredients: Ingredient[]): InventoryDraft {
  const lista = candidatos(ingredients);
  const usados = new Set<string>();
  const lines: InventoryDraftLine[] = [];
  const sinEmparejar: InventoryDraft['sinEmparejar'] = [];
  const avisos: string[] = [];

  for (const row of sheet.rows) {
    const cells = row.cells.map((c) => c.trim()).filter((c) => c !== '');
    if (cells.length === 0) continue;
    // Cabecera de la tabla o titulo de la hoja: fuera.
    const primera = normalizarTexto(cells[0]);
    if (CABECERAS.includes(primera) || primera.startsWith('recuento de existencias')) continue;

    // El nombre es la primera celda con letras; la cantidad, la ULTIMA celda
    // numerica (la caja de escribir esta a la derecha del formato).
    const nombre = cells.find((c) => /[a-záéíóúñ]/i.test(c));
    if (!nombre) continue;
    const numericas = cells.filter((c) => c !== nombre && esNumerica(c));
    const leido = numericas.length > 0 ? numericas[numericas.length - 1] : undefined;

    const match = emparejar(nombre, lista, usados);
    if (!match) {
      sinEmparejar.push({ texto: nombre, leido });
      continue;
    }
    usados.add(match.cand.ing.ingredientId);
    const qty = parseCantidad(leido);
    const conf = row.confidence ?? 100;
    let confianza: ConfianzaLinea = 'alta';
    if (!match.exacto || (leido !== undefined && qty === undefined)) confianza = 'baja';
    else if (conf < 80 || (leido !== undefined && /[.,]/.test(leido) && conf < 92)) confianza = 'media';

    lines.push({
      ingredientId: match.cand.ing.ingredientId,
      name: ingredientDisplayName(match.cand.ing),
      formato: purchaseFormatLabel(match.cand.ing),
      qty,
      leido,
      confianza,
    });
  }

  if (lines.length === 0) {
    avisos.push(
      'No hemos reconocido ningún ingrediente en la hoja. Comprueba que es la hoja de recuento de Alexia y que la foto se lee bien.',
    );
  }
  const dudosas = lines.filter((l) => l.confianza === 'baja').length;
  if (dudosas > 0) {
    avisos.push(
      `${dudosas} ${dudosas === 1 ? 'línea es dudosa' : 'líneas son dudosas'}: revisa las marcadas antes de pasar al recuento.`,
    );
  }
  if (sinEmparejar.length > 0) {
    avisos.push(
      `${sinEmparejar.length} ${sinEmparejar.length === 1 ? 'fila de la hoja no coincide' : 'filas de la hoja no coinciden'} con ningún ingrediente.`,
    );
  }

  return { lines, sinEmparejar, avisos };
}
